import { z } from 'zod'

import { paginacaoSearchFields, toLimitOffset } from '@/widgets/data-table/table-search'

import type { InstallmentPlanKind, InstallmentPlanListParams } from './api'

/**
 * Search params das telas de Parcelamentos.
 *
 * As duas rotas (a pagar / a receber) compartilham este schema — o `kind` vem
 * da rota, não da query string: ele é a identidade da tela, não uma escolha do
 * usuário. Por isso o `validateSearch` fica repetido nas duas rotas e o
 * componente recebe o `search` por prop.
 */
export const parcelamentosSearchSchema = z.object({
  ...paginacaoSearchFields,
  q: z.string().min(1).optional(),
  contato: z.uuid().optional(),
  de: z.iso.date().optional(),
  ate: z.iso.date().optional(),
  ordenar: z
    .enum([
      'first_due_date',
      '-first_due_date',
      'created_at',
      '-created_at',
      'total_amount',
      '-total_amount',
    ])
    .default('-first_due_date'),
})

export type ParcelamentosSearch = z.infer<typeof parcelamentosSearchSchema>

/** Default do backend (installment_plan_service._ORDER_BY_DEFAULT). */
export const PARCELAMENTOS_ORDEM_DEFAULT =
  '-first_due_date' satisfies ParcelamentosSearch['ordenar']

/** Chaves de filtro — o que "limpar filtros" zera. */
export const PARCELAMENTOS_FILTROS = [
  'q',
  'contato',
  'de',
  'ate',
] as const satisfies readonly (keyof ParcelamentosSearch)[]

export function toInstallmentPlanListParams(
  s: ParcelamentosSearch,
  kind: InstallmentPlanKind,
): InstallmentPlanListParams {
  return {
    kind,
    ...(s.q !== undefined && { search: s.q }),
    ...(s.contato !== undefined && { stakeholder_id: s.contato }),
    // O período filtra o PRIMEIRO vencimento do plano, não o das parcelas: é o
    // que identifica "os parcelamentos que começaram em julho".
    ...(s.de !== undefined && { first_due_from: s.de }),
    ...(s.ate !== undefined && { first_due_to: s.ate }),
    order_by: s.ordenar,
    ...toLimitOffset(s),
  }
}
